import { Injectable } from '@angular/core';
import {
  ConfigBaseFilter,
  ConfigService,
  ListLayout,
  ListLayoutMultipleComponent,
  ListSortOption,
  PimTemplateProperties,
  SimpleConfigBaseFilter
} from 'gung-list';
import { ColmecWorkingOrderService } from './colmec-working-order.service';
import { BehaviorSubject, Observable, combineLatest, first, forkJoin, of, switchMap } from 'rxjs';
import { ColmecWorkOrder } from '../components/working-order/colmec-working-order-wrapper/colmec-working-order-wrapper.component';
import { AuthService, ColumnSortSelectionService, Customer, CustomerService } from 'gung-standard';
import { ColmecWorkingOrderTableViewComponent } from '../components/working-order/colmec-working-order-table-view/colmec-working-order-table-view.component';
import { format } from 'date-fns';
import { ColmecPartnerAgreement } from './colmec-partner-agreements.service';

export class ColmecWorkOrderVehicleNoFilter extends SimpleConfigBaseFilter<ColmecWorkOrder> {
  getName(): string {
    return 'REG_NR';
  }

  getOptionIds(item: ColmecWorkOrder): string[] {
    if (item?.regNr) {
      return [item.regNr];
    }
    return [];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class ColmecWorkOrderStateFilter extends SimpleConfigBaseFilter<ColmecWorkOrder> {
  getName(): string {
    return 'WORK_ORDER_STATE';
  }

  getOptionIds(item: ColmecWorkOrder): string[] {
    if (item?.workState) {
      return [item.workState];
    }
    return ['NO_STATE_AVAILABLE'];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class ColmecWorkOrderDateFilter extends SimpleConfigBaseFilter<ColmecWorkOrder> {
  type = 'dateRangeFilter';

  getName(): string {
    return 'DATE';
  }

  getOptionIds(item: ColmecWorkOrder): string[] {
    return [format(new Date(item.workPerformedDate), 'yyMMdd')];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class ColmecWorkOrderPartnerFilter extends SimpleConfigBaseFilter<ColmecWorkOrder> {
  constructor(
    protected customerList: Customer[],
    protected agreementsList: ColmecPartnerAgreement[]
  ) {
    super();
  }

  getName(): string {
    return 'PARTNER';
  }

  getOptionIds(item: ColmecWorkOrder): string[] {
    const agreement = this.agreementsList.find(a => a.id === item.agreementId);

    if (!!agreement) {
      return [agreement.partnerId];
    } else {
      return ['UNDEFINED'];
    }
  }

  getOptionName(key: string): string {
    const customer = this.customerList.find(c => c.id === key);

    if (!!customer && key !== 'UNDEFINED') {
      return customer.name;
    } else {
      return key;
    }
  }
}

export class ColmecWorkOrderCustomerFilter extends SimpleConfigBaseFilter<ColmecWorkOrder> {
  constructor(
    protected customerList: Customer[],
    protected agreementsList: ColmecPartnerAgreement[]
  ) {
    super();
  }

  getName(): string {
    return 'CUSTOMER';
  }

  getOptionIds(item: ColmecWorkOrder): string[] {
    const agreement = this.agreementsList.find(a => a.id === item.agreementId);

    if (!!agreement) {
      return [agreement.customerId];
    } else {
      return ['UNDEFINED'];
    }
  }

  getOptionName(key: string): string {
    const customer = this.customerList.find(c => c.id === key);

    if (!!customer && key !== 'UNDEFINED') {
      return customer.name;
    } else {
      return key;
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class ColmecWorkingOrderListConfigService implements ConfigService<ColmecWorkOrder> {
  topFilter = false;

  public isAdmin: boolean = false;
  public isSales: boolean = false;
  public partnerId: string = '';

  public customers: Customer[] = [];
  public agreements: ColmecPartnerAgreement[] = [];

  public dynamicColumns?: BehaviorSubject<PimTemplateProperties[]> = new BehaviorSubject<PimTemplateProperties[]>([]);

  constructor(
    protected colmecWorkingOrderService: ColmecWorkingOrderService,
    protected authService: AuthService,
    protected customerService: CustomerService,
    protected columnSortSelectionService: ColumnSortSelectionService
  ) { }

  getFilters(): ConfigBaseFilter<ColmecWorkOrder>[] {
    const filters = [
      new ColmecWorkOrderVehicleNoFilter(),
      new ColmecWorkOrderStateFilter(),
      new ColmecWorkOrderDateFilter()
    ];

    if (this.isSales) {
      filters.push(new ColmecWorkOrderPartnerFilter(this.customers, this.agreements));
      filters.push(new ColmecWorkOrderCustomerFilter(this.customers, this.agreements));
    }

    return filters;
  }

  getSortOptions(): ListSortOption<ColmecWorkOrder>[] {
    // TODO: implement
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getLayouts(): ListLayout<ColmecWorkOrder>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => ColmecWorkingOrderTableViewComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'WorkOrdersView'
      }
    ];
  }

  getItems(): Observable<ColmecWorkOrder[]> {
    // Use combine latest when you need something like forkJoin but need to get updates from 2 or more sources
    return combineLatest({
      selectedColumnSort: this.columnSortSelectionService.getSelectedColumnSort(),
      workOrders: this.colmecWorkingOrderService.getListWorkingOrders(this.isAdmin || this.isSales, this.partnerId)
    }).pipe(
      switchMap(({ selectedColumnSort, workOrders }) =>
        forkJoin({
          selectedColumnSort: of(selectedColumnSort),
          workOrders: of(workOrders),
          customers: this.customers.length > 0 ? of(this.customers) : this.customerService.getCustomers().pipe(first())
        })
      ),
      switchMap(({ selectedColumnSort, workOrders, customers }) => {
        if (this.customers.length === 0) {
          this.customers = customers;
        }

        for (const workOrder of workOrders) {
          const cId: string = workOrder.agreementId?.split('@')?.[0] || '';
          const pId: string = workOrder.agreementId?.split('@')?.[1]?.split('_')?.[0] || '';

          let currency: string = '';
          let totalPrice: number = 0;

          for (const tire of workOrder.tires) {
            totalPrice += tire.price.value * tire.qty;
            if (!currency) {
              currency = tire.price.currency || '';
            }
          }

          for (const service of workOrder.services) {
            totalPrice += (service.price.value || 0) * service.qty;
          }

          for (const extra of workOrder.extras) {
            totalPrice += (+extra.price || 0) * (extra.extra?.qty || 1);
          }

          workOrder.extra._gung = {
            customerName: customers.find(c => c.id === cId)?.name || cId,
            partnerName: customers.find(c => c.id === pId)?.name || pId,
            totalPrice: totalPrice,
            currency: currency
          };
        }

        if (!this.dynamicColumns || this.dynamicColumns?.value?.length === 0) {
          this.dynamicColumns.next(
            this.buildDummyDynamicColumns([
              { path: 'id', translationKey: 'WORK_ORDER_ID' },
              { path: 'extra._gung.partnerName', translationKey: 'PARTNER' },
              { path: 'extra._gung.customerName', translationKey: 'CUSTOMER' },
              { path: 'workPerformedDate', translationKey: 'DATE' },
              { path: 'regNr', translationKey: 'REG_NR' },
              { path: 'extra._gung.totalPrice', translationKey: 'TOTAL_PRICE' },
              { path: 'workState', translationKey: 'WORK_ORDER_STATE' }
            ])
          );
        }

        if (!!selectedColumnSort) {
          workOrders = this.columnSortSelectionService.sortItemsByColumn(workOrders, selectedColumnSort, true);
        }

        return of(workOrders);
      })
    );
  }

  getItemId(item: ColmecWorkOrder) {
    return item.id;
  }

  getSearchTerms(item: ColmecWorkOrder) {
    return [item.id, item.regNr, item.agreementId];
  }

  getNothingFoundTranslateTag(): string {
    return '';
  }

  protected buildDummyDynamicColumns(data: { path: string; translationKey: string }[]): PimTemplateProperties[] {
    const result: PimTemplateProperties[] = [];

    for (const item of data) {
      result.push({
        path: item.path,
        type: undefined,
        metadata: undefined,
        isDisplay: true,
        isDisplayList: true,
        isDisplayGrid: true,
        isDisplayDetails: false,
        isFilter: false,
        translationKey: item.translationKey,
        metaReference: undefined
      });
    }

    return result;
  }
}
